import React from "react";
import { useNavigate } from "react-router-dom";

import LightIcon from "/static/icons/light.svg";
import ArrowIcon from "/static/icons/arrow-right.svg";

/* ---------------- TYPES ---------------- */
interface InsightCardType {
    title: string;
    priority: "HIGH" | "MEDIUM" | "LOW";
    description: string;
    suggestion: string;

    onClick?: () => void; // <-- ADD THIS
}

/* ---------------- DATA (ALL ITEMS) ---------------- */
const insightsData: InsightCardType[] = [
    {
        title: "AI Seasonal Opportunity Predictor",
        priority: "HIGH",
        description:
            "AI detected upcoming seasonal demand fluctuations. Your store can gain early advantage by optimizing listings and running targeted offers.",
        suggestion: "Prepare listings and campaigns for predicted seasonal demand.",
    },
    {
        title: "AI Auto-Optimization Engine",
        priority: "HIGH",
        description: "Your product performance can be improved by automated adjustments to pricing, tags, SEO, and placement.",
        suggestion: "Enable automated optimization rules to boost performance.",
    },
    {
        title: "AI Listing Quality Improvement",
        priority: "MEDIUM",
        description: "Several products have missing or weak titles, descriptions, or keywords.",
        suggestion: "Improve listing quality for higher conversions.",
    },
    {
        title: "AI Conversion Funnel Diagnosis",
        priority: "HIGH",
        description: "AI identified drop-offs in your product → cart → checkout funnel.",
        suggestion: "Fix bottlenecks to improve conversion rate.",
    },
    {
        title: "AI Product Ranking Booster",
        priority: "HIGH",
        description: "Ranking fluctuations detected due to low engagement and fewer recent updates.",
        suggestion: "Refresh listings to increase ranking.",
    },
    {
        title: "AI High-Potential Product Detector",
        priority: "HIGH",
        description: "Some products show a steep increase in impressions but low conversions.",
        suggestion: "Review pricing and content for high-potential items.",
    },
    {
        title: "AI Geo Expansion Suggestion",
        priority: "LOW",
        description: "Your products have emerging demand in new regions or countries.",
        suggestion: "Enable multi-region listing to expand visibility.",
    },
    {
        title: "AI Demand Surge & Trend Alert",
        priority: "HIGH",
        description: "Spike in searches and interest for your category detected.",
        suggestion: "Stock up and adjust pricing for trending items.",
    },
    {
        title: "AI Slow-Moving Inventory Solver",
        priority: "MEDIUM",
        description: "Several products have minimal sales over the last 30 days.",
        suggestion: "Run discount or bundle offers to clear stock.",
    },
    {
        title: "AI Returns & Cancellation Cause Predictor",
        priority: "LOW",
        description: "AI detected patterns in returns that can be prevented by improving clarity.",
        suggestion: "Fix misleading details to reduce returns.",
    },
    {
        title: "AI VIP & Repeat Customer Win-back",
        priority: "HIGH",
        description: "Some loyal customers haven't returned recently.",
        suggestion: "Send targeted offers to win them back.",
    },
    {
        title: "AI Marketing Best Timing",
        priority: "MEDIUM",
        description: "AI identified the best times your customers are active.",
        suggestion: "Schedule posts and discounts at optimal times.",
    },
    {
        title: "AI Competitive Benchmarking",
        priority: "HIGH",
        description: "Competitors changed pricing and promotions.",
        suggestion: "Adjust strategy to stay competitive.",
    },
    {
        title: "AI Keyword & SEO Opportunity Finder",
        priority: "MEDIUM",
        description: "New trending keywords have emerged for your category.",
        suggestion: "Update SEO metadata to gain traffic.",
    },
    {
        title: "AI Competitor Behavior & Pricing Shift Radar",
        priority: "HIGH",
        description: "Competitors are rapidly adjusting prices or stock.",
        suggestion: "Monitor competition and update strategy.",
    },
];

/* ---------------- CARD COMPONENT ---------------- */
const AiInsightCard = ({ title, priority, description, suggestion, onClick }: InsightCardType) => {
    return (
        <div className="ai_card" onClick={onClick}>
            <div className="ai_left_icon">
                <img src="/static/images/placeholder.png" alt="icon" />
            </div>

            <div className="ai_content">
                <div className="ai_title_row">
                    <h3>{title}</h3>

                    <div className="ai_tags">
                        <span className={`priority_tag ${priority.toLowerCase()}`}>
                            {priority}
                        </span>

                        <span className="flash_icon">
                            <img src="/static/images/flash.svg" alt="flash" />
                        </span>
                    </div>
                </div>

                <p className="ai_description">{description}</p>

                <div className="ai_recommend_bar">
                    <img src="/static/images/ai_icon.svg" alt="spark" />
                    <span>{suggestion}</span>
                </div>
            </div>

        </div>
    );
};

/* ---------------- MAIN PAGE ---------------- */
const AiInsights = () => {
    const navigate = useNavigate();

    return (
        <>
            <div className="main_title">
                <h1>AI Insights</h1>
                <p>
                    <button onClick={() => navigate("/dashboard")}>Dashboard</button> - AI Insights
                </p>
            </div>

            <div className="cards ai_insights_list">
                {insightsData.map((item, idx) => (
                    <AiInsightCard onClick={() => navigate("/ai-insights/view")} key={idx} {...item} />
                ))}
            </div>
        </>
    );
};

export default AiInsights;

